import { Grid, Stack, Text, Title } from "@mantine/core";
import { FC, useEffect, useMemo, useState } from "react";

import { Navigate, useNavigate, useOutletContext } from "react-router-dom";
import { BankHoliday, isValidPatient, Product } from "@jasper/client-models";
import {
  useOrderWorkflow,
  StepNavigation,
  isValidProduct,
} from "@jasper/product-workflows";
import { PatientSummary } from "./PatientSummary";
import { ProductsSummary } from "./ProductSummary";
import { OrderDelivery } from "./OrderDelivery";
import { OrderComment } from "./OrderComment";
import { openConfirmModal } from "@mantine/modals";
import { OrderSummaryV2 } from "./v2/OrderSummaryV2";
import { getItemTypeLabel, isBeforeDay } from "@jasper/shared";
import {
  hasGraphQLErrorCode,
  useOrderConfiguration,
  useRequestOrderDesign,
} from "@jasper/use-cases";
import { useFeatureFlagEnabled } from "posthog-js/react";

const DESIGN_RUSH_MESSAGE =
  "La demande d'un design ne permet pas de bénéficier d'un délai réduit.";

const OrderSummaryV1 = () => {
  const navigate = useNavigate();
  const { orderId } = useOutletContext<{ orderId: string }>();
  const {
    order,
    isLoading,
    error,
    removeOrderProduct,
    getEstimatedDeliveryDate,
    getBankHolidays,
    confirmOrder,
  } = useOrderConfiguration({ orderId });
  const { goNext, goBack } = useOrderWorkflow({ orderId });

  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);
  const [deliveryDate, setDeliveryDate] = useState<Date>();
  const [estimatedOptimalDeliveryDate, setEstimatedOptimalDeliveryDate] =
    useState<Date>();
  const [estimatedRushDeliveryDate, setEstimatedRushDeliveryDate] =
    useState<Date>();
  const [deliveryDateTooSoonError, setDeliveryDateTooSoonError] =
    useState(false);

  const { requestOrderDesign } = useRequestOrderDesign();
  const isDesignRequestEnabled = useFeatureFlagEnabled("order_design_request");
  const [isDesignRequested, setIsDesignRequested] = useState(false);

  const products: Product[] = useMemo(() => {
    if (order?.products?.every(product => isValidProduct(product))) {
      return order.products as Product[];
    }

    return [];
  }, [order]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (order?.deliveryDate && !deliveryDate) {
      setDeliveryDate(new Date(order.deliveryDate));
    }

    void getEstimatedDeliveryDate().then(
      ({ estimatedOptimalDeliveryDate, estimatedRushDeliveryDate }) => {
        if (!deliveryDate && !order?.deliveryDate) {
          setDeliveryDate(new Date(estimatedOptimalDeliveryDate));
        }

        setEstimatedOptimalDeliveryDate(new Date(estimatedOptimalDeliveryDate));
        setEstimatedRushDeliveryDate(new Date(estimatedRushDeliveryDate));
      }
    );
  }, [order?.deliveryDate, isLoading]);

  useEffect(() => {
    // Beginning of current month
    const from = new Date();
    from.setDate(1);
    from.setHours(0, 0, 0, 0);

    // End of next month
    const to = new Date();
    to.setMonth(to.getMonth() + 2);
    to.setDate(0);
    to.setHours(23, 59, 59, 999);

    getBankHolidays(from, to).then(bankHolidays => {
      setBankHolidays(bankHolidays);
    });
  }, []);

  const isRushDate = (date: Date) =>
    !!estimatedOptimalDeliveryDate &&
    isBeforeDay(date, estimatedOptimalDeliveryDate);

  const handleDesignRequestedToggle = () => {
    if (isDesignRequested) {
      setIsDesignRequested(false);
      return;
    }

    if (
      deliveryDate &&
      estimatedOptimalDeliveryDate &&
      isRushDate(deliveryDate)
    ) {
      openConfirmModal({
        title: "Demande de confirmation",
        children: (
          <Text>
            {DESIGN_RUSH_MESSAGE} En confirmant, la date de livraison sera mise
            à jour sur la date optimale.
          </Text>
        ),
        labels: { confirm: "Confirmer", cancel: "Annuler" },
        onConfirm: () => {
          setDeliveryDate(estimatedOptimalDeliveryDate);
          setDeliveryDateTooSoonError(false);
          setIsDesignRequested(true);
        },
      });
      return;
    }

    setIsDesignRequested(true);
  };

  const handleDeliveryDateChange = (newDeliveryDate: Date) => {
    if (isDesignRequested && isRushDate(newDeliveryDate)) {
      openConfirmModal({
        title: "Demande de confirmation",
        children: (
          <Text>
            {DESIGN_RUSH_MESSAGE} En confirmant, la demande de design sera
            annulée et la date de livraison mise à jour.
          </Text>
        ),
        labels: { confirm: "Confirmer", cancel: "Annuler" },
        onConfirm: () => {
          setIsDesignRequested(false);
          setDeliveryDate(newDeliveryDate);
          setDeliveryDateTooSoonError(false);
        },
      });
      return;
    }

    setDeliveryDate(newDeliveryDate);
    setDeliveryDateTooSoonError(false);
  };

  const handleConfirmOrder = async () => {
    if (
      !deliveryDate ||
      !estimatedOptimalDeliveryDate ||
      !estimatedRushDeliveryDate
    ) {
      return;
    }

    const isRush = deliveryDate < estimatedOptimalDeliveryDate;

    try {
      await confirmOrder({ deliveryDate, isRush });
    } catch (err) {
      if (hasGraphQLErrorCode(err, "ORDER_DELIVERY_DATE_TOO_SOON")) {
        setDeliveryDateTooSoonError(true);
        return;
      }

      throw err;
    }

    if (isDesignRequested) {
      await requestOrderDesign(orderId).catch(err => {
        console.error("Error while requesting order design", err);
      });
    }

    goNext();
  };

  if (isLoading || error || !order) {
    return null;
  }

  if (!isValidPatient(order.patient)) {
    return <Navigate to={`../order-info`} replace />;
  }

  if (products.length !== order.products?.length) {
    const firstInvalidProduct = order.products?.find(
      product => !product || !isValidProduct(product)
    );

    return (
      <Navigate
        to={`../products/${firstInvalidProduct?.id ?? "new"}`}
        replace
      />
    );
  }

  return (
    <Stack h="100%" p="md">
      <Title order={2} c="primary">
        Confirmation de la commande
      </Title>
      <Grid gap="xs" style={{ flex: 1 }}>
        <Grid.Col span={8}>
          <Stack gap="md" h="100%" justify="space-between">
            <Stack gap="md">
              <PatientSummary
                patient={order.patient}
                onEdit={() => navigate(`../order-info`)}
              />
              <ProductsSummary
                products={products}
                onEdit={productId => navigate(`../products/${productId}`)}
                onAdd={() => navigate("../products/add")}
                onDelete={productId => {
                  const product = products.find(
                    product => product.id === productId
                  );
                  if (!product) return;

                  openConfirmModal({
                    title: "Supprimer la prothèse",
                    children: `Vous êtes sur le point de supprimer de votre commande une prothèse de type ${getItemTypeLabel(product.type as any)}. Êtes-vous sûr de vouloir continuer ?`,
                    labels: { confirm: "Supprimer", cancel: "Annuler" },
                    confirmProps: { color: "red" },
                    onConfirm: () => removeOrderProduct(productId),
                  });
                }}
              />
            </Stack>
            <StepNavigation onBack={goBack} />
          </Stack>
        </Grid.Col>
        <Grid.Col span="auto">
          <Stack gap="md">
            <OrderDelivery
              deliveryDate={deliveryDate}
              onDeliveryDateChange={handleDeliveryDateChange}
              estimatedOptimalDeliveryDate={estimatedOptimalDeliveryDate}
              estimatedRushDeliveryDate={estimatedRushDeliveryDate}
              deliveryDateTooSoonError={deliveryDateTooSoonError}
              bankHolidays={bankHolidays}
              onConfirmOrder={handleConfirmOrder}
              showDesignRequest={!!isDesignRequestEnabled}
              isDesignRequested={isDesignRequested}
              onDesignRequestedToggle={handleDesignRequestedToggle}
              onMonthChange={date => {
                const from = new Date(date);
                from.setDate(1);
                from.setHours(0, 0, 0, 0);

                const to = new Date(date);
                to.setMonth(to.getMonth() + 2);
                to.setDate(0);
                to.setHours(23, 59, 59, 999);

                getBankHolidays(from, to).then(newBankHolidays => {
                  // Set the newly fetched bank holidays for the new month, but also keep the previously fetched bank holidays to avoid losing them when navigating between months
                  const uniqueBankHolidays = [
                    ...newBankHolidays,
                    ...bankHolidays.filter(
                      prevBankHoliday =>
                        !newBankHolidays.some(
                          bankHoliday =>
                            bankHoliday.startDate ===
                              prevBankHoliday.startDate &&
                            bankHoliday.endDate === prevBankHoliday.endDate &&
                            bankHoliday.name === prevBankHoliday.name
                        )
                    ),
                  ];
                  setBankHolidays(uniqueBankHolidays);
                });
              }}
            />
            <OrderComment orderId={orderId} />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export const OrderSummary: FC = () => {
  const { orderId } = useOutletContext<{ orderId: string }>();
  // Falls back to V1 when PostHog can't load the flag, so the order can still be sent
  const isThemeV2Enabled = useFeatureFlagEnabled("theme_v2", false);

  // Keyed by orderId so the order-scoped state (delivery date, design request…) resets when switching orders
  return isThemeV2Enabled ? (
    <OrderSummaryV2 key={orderId} />
  ) : (
    <OrderSummaryV1 />
  );
};
